import { apiFetch } from '@/api/client';
import type { Brief, BriefStatus } from '@/types';

export interface BriefUpdateInput {
  title?: string;
  angle?: string;
  aeo_intent?: string;
  target_keywords?: string[];
  suggested_structure?: string[];
  word_count_target?: number;
}

export interface BriefsListOptions {
  status?: BriefStatus;
  includeArchived?: boolean;
  q?: string;
  sort?: 'date' | 'title' | 'status';
  order?: 'asc' | 'desc';
}

export interface BriefDeleteResponse {
  status: 'archived' | 'deleted';
  id: string;
}

export const briefs = {
  list: (siteId: string, opts: BriefsListOptions | BriefStatus = {}) => {
    const params = new URLSearchParams({ site_id: siteId });
    const o = typeof opts === 'string' ? { status: opts } : opts;
    if (o.status) params.set('status', o.status);
    if (o.includeArchived) params.set('include_archived', 'true');
    if (o.q) params.set('q', o.q);
    if (o.sort) params.set('sort', o.sort);
    if (o.order) params.set('order', o.order);
    return apiFetch<Brief[]>(`/briefs/?${params.toString()}`);
  },
  update: (id: string, patch: BriefUpdateInput) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  approve: (id: string) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    }),
  reject: (id: string) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
    }),
  clone: (id: string) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}/clone`, {
      method: 'POST',
    }),
  remove: (id: string, opts: { hard?: boolean } = {}) =>
    apiFetch<BriefDeleteResponse>(
      `/briefs/${encodeURIComponent(id)}${opts.hard ? '?hard=true' : ''}`,
      { method: 'DELETE' },
    ),
  reopen: (id: string) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}/reopen`, {
      method: 'POST',
    }),
  withdraw: (id: string) =>
    apiFetch<Brief>(`/briefs/${encodeURIComponent(id)}/withdraw`, {
      method: 'POST',
    }),
  bulkArchive: (ids: string[]) =>
    apiFetch<{ archived: number }>(`/briefs/bulk`, {
      method: 'POST',
      body: JSON.stringify({ action: 'archive', ids }),
    }),
  exportCsvUrl: (siteId: string) =>
    `/api/briefs/?${new URLSearchParams({ site_id: siteId, format: 'csv' }).toString()}`,
};
